import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DashboardService } from '../../../services/dashboard.service';
import { getWidget, WidgetDef } from '../registry';

interface Params {
    institutionId: string;
    boardKey: string;
    fallbackIds: string[];
    editable: boolean;
}

export default function useDashboardLayout({ institutionId, boardKey, fallbackIds, editable }: Params) {
    const [order, setOrder] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);

    const clean = useCallback((ids: string[]) => ids.filter(id => getWidget(id) !== undefined), []);

    // Read the latest fallback from effects/callbacks WITHOUT making the array's identity a
    // dependency: callers may pass a fresh literal on some renders. A derived primitive
    // `fallbackKey` drives effect re-runs (so a role-preview switch still refreshes the shown
    // preset) while the ref hands the effect the current array.
    const fallbackRef = useRef(fallbackIds);
    fallbackRef.current = fallbackIds;
    const fallbackKey = fallbackIds.join('|');

    useEffect(() => {
        let live = true;
        setLoading(true);
        if (!editable) {
            // Role preview / read-only: show the fallback, never touch storage.
            setOrder(clean(fallbackRef.current));
            setLoading(false);
            return () => { live = false; };
        }
        DashboardService.getLayout(institutionId, boardKey)
            .then(l => {
                if (!live) return;
                const saved = clean(l.widgetIds);
                setOrder(saved.length > 0 ? saved : clean(fallbackRef.current));
            })
            .catch(() => { if (live) setOrder(clean(fallbackRef.current)); })
            .finally(() => { if (live) setLoading(false); });
        return () => { live = false; };
    }, [institutionId, boardKey, editable, clean, fallbackKey]);

    const persist = useCallback((next: string[]) => {
        if (editable) DashboardService.saveLayout(institutionId, boardKey, { widgetIds: next }).catch(() => {});
    }, [institutionId, boardKey, editable]);

    const commit = useCallback((next: string[]) => {
        setOrder(next);
        persist(next);
    }, [persist]);

    const add = useCallback((id: string) => {
        if (!editable) return;
        setOrder(prev => {
            if (prev.includes(id) || !getWidget(id)) return prev;
            const next = [...prev, id];
            persist(next);
            return next;
        });
    }, [editable, persist]);

    const remove = useCallback((id: string) => {
        if (!editable) return;
        setOrder(prev => {
            const next = prev.filter(x => x !== id);
            persist(next);
            return next;
        });
    }, [editable, persist]);

    const reorder = useCallback((from: number, to: number) => {
        if (!editable) return;
        setOrder(prev => {
            const next = [...prev];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            persist(next);
            return next;
        });
    }, [editable, persist]);

    const reset = useCallback(() => {
        if (!editable) return;
        commit(clean(fallbackRef.current));
    }, [editable, clean, commit]);

    const widgets = useMemo<WidgetDef[]>(
        () => order.map(getWidget).filter((w): w is WidgetDef => w !== undefined),
        [order],
    );

    return { order, widgets, add, remove, reorder, reset, loading };
}
